// Load the chemistry doc (YAML at build time) and instantiate a sandbox
// backed by either the wasm core (if present) or the JS fallback.

import bundledYaml from "../../../../packages/material-db/data/bundled.yaml?raw";

import { JsFallbackSandbox } from "./jsFallback";
import type { MaterialDef, SandboxBundle, SandboxLike } from "./types";
import { parseChemDoc } from "./yaml";

interface SandboxLoadOptions {
  width: number;
  height: number;
  seed: number;
}

export async function loadSandbox(opts: SandboxLoadOptions): Promise<SandboxBundle> {
  const doc = parseChemDoc(bundledYaml);
  const materials = ensureEmptyFirst(doc.materials);
  const wasm = await tryLoadWasm(opts, bundledYaml, materials);
  if (wasm) return { sandbox: wasm, materials, backend: "wasm" };
  console.warn(
    "[realchem] running on the pure-JS fallback sandbox.\n" +
      "          The fallback only does falling-sand + heat diffusion;\n" +
      "          there is no chemistry, fuel pass, or wind-emission, so\n" +
      "          fire/wood/water reactions will look broken.\n" +
      "          Run `npm run wasm:build` to enable the wasm engine.",
  );
  const js = new JsFallbackSandbox(opts.width, opts.height, materials, opts.seed);
  return { sandbox: js, materials, backend: "js" };
}

function ensureEmptyFirst(materials: MaterialDef[]): MaterialDef[] {
  if (materials[0]?.id === "empty") return materials;
  const empty: MaterialDef = {
    id: "empty",
    name: "Empty",
    phase: "gas",
    behavior: "static",
    color: [0, 0, 0],
  };
  return [empty, ...materials.filter((m) => m.id !== "empty")];
}

async function tryLoadWasm(
  opts: SandboxLoadOptions,
  yaml: string,
  materials: MaterialDef[],
): Promise<SandboxLike | null> {
  try {
    // Real dynamic `import()` so Vite can trace it — without that, the
    // wasm-bindgen output never lands in `dist/` for production builds
    // and the prod site silently falls back to the JS sandbox. The
    // `predev` / `prebuild` npm hooks (`scripts/ensure-wasm.mjs`)
    // guarantee `src/wasm/realchem.js` exists before tsc/vite run.
    const mod = (await import("../wasm/realchem.js")) as unknown as {
      default: (input?: unknown) => Promise<unknown>;
      Sandbox: new (
        w: number,
        h: number,
        seed: number,
        chemYaml: string,
        behaviorsJson: string,
      ) => WasmSandbox;
    };
    await mod.default();
    const behaviors = JSON.stringify(materials.map((m) => m.behavior ?? "static"));
    const inner = new mod.Sandbox(opts.width, opts.height, opts.seed, yaml, behaviors);
    return wrapWasmSandbox(inner);
  } catch (err) {
    console.warn("wasm sandbox unavailable, falling back to JS:", err);
    return null;
  }
}

interface WasmSandbox {
  width(): number;
  height(): number;
  material_count(): number;
  material_id(id: string): number;
  paint_circle(cx: number, cy: number, r: number, material: number): void;
  paint_cell_state(x: number, y: number, material: number, state: number): void;
  heat_circle(cx: number, cy: number, r: number, kelvin: number): void;
  tick(dt: number): void;
  snapshot_materials(): Uint16Array;
  snapshot_seeds(): Uint8Array;
  snapshot_temperature(): Float32Array;
  tick_count(): bigint;
  run_experiment_json(json: string): string;
  snapshot_experiment_json(name: string): string;
  clear_all(ambient_k: number): void;
  set_ambient_temperature(ambient_k: number): void;
  toggle_at(x: number, y: number): boolean;
  is_trigger_at(x: number, y: number): boolean;
  set_key_input(key: number): void;
  is_code_at(x: number, y: number): boolean;
  code_at(x: number, y: number): string;
  code_load(x: number, y: number, src: string): string;
  layer_count(): number;
  layer_width(i: number): number;
  layer_height(i: number): number;
  snapshot_layer(i: number): Uint16Array;
  snapshot_layer_seeds(i: number): Uint8Array;
  current_layer(): number;
  layer_depth(): number;
  is_dimension_at(x: number, y: number): boolean;
  enter_dimension(x: number, y: number): boolean;
  exit_dimension(): boolean;
}

function wrapWasmSandbox(s: WasmSandbox): SandboxLike {
  return {
    width: () => s.width(),
    height: () => s.height(),
    materialCount: () => s.material_count(),
    materialId: (id) => s.material_id(id),
    paintCircle: (cx, cy, r, m) => s.paint_circle(cx, cy, r, m),
    paintCellState: (x, y, m, state) => s.paint_cell_state(x, y, m, state),
    heatCircle: (cx, cy, r, k) => s.heat_circle(cx, cy, r, k),
    tick: (dt) => s.tick(dt),
    snapshotMaterials: () => s.snapshot_materials(),
    snapshotSeeds: () => s.snapshot_seeds(),
    snapshotTemperature: () => s.snapshot_temperature(),
    tickCount: () => Number(s.tick_count()),
    runExperiment: (json) => s.run_experiment_json(json),
    snapshotExperiment: (name) => s.snapshot_experiment_json(name),
    clearAll: (ambientK) => s.clear_all(ambientK),
    setAmbientTemperature: (ambientK) => s.set_ambient_temperature(ambientK),
    toggleAt: (x, y) => s.toggle_at(x, y),
    isTriggerAt: (x, y) => s.is_trigger_at(x, y),
    setKeyInput: (key) => s.set_key_input(key),
    isCodeAt: (x, y) => s.is_code_at(x, y),
    codeAt: (x, y) => s.code_at(x, y),
    codeLoad: (x, y, src) => s.code_load(x, y, src),
    layerCount: () => s.layer_count(),
    layerWidth: (i) => s.layer_width(i),
    layerHeight: (i) => s.layer_height(i),
    snapshotLayer: (i) => s.snapshot_layer(i),
    snapshotLayerSeeds: (i) => s.snapshot_layer_seeds(i),
    currentLayer: () => s.current_layer(),
    layerDepth: () => s.layer_depth(),
    isDimensionAt: (x, y) => s.is_dimension_at(x, y),
    enterDimension: (x, y) => s.enter_dimension(x, y),
    exitDimension: () => s.exit_dimension(),
  };
}
